import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { UserList } from '../../components/ui/list/UserList';
import Pagination from '../../components/atoms/Pagination';
import { headerUserTable } from '../../utils/constants';
import { GETUsers } from '../../services/users';
import { IUser } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import CustomerFilter, {
  CustomerDateFilterData,
} from '../../components/ui/filters/customer';
import withComponentAuth from '../../hoc/withComponentAuth';
import CreateUserModal from '../Booking/actions/AddUserModal';
import { useUrlSearchQuery } from '../../hooks/useUrlSearchQuery';

const UserManagement = () => {
  const [page, setPage] = useState(1); // Starting from page 1
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [users, setUsers] = useState<IUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [sort, setSort] = useState<'ASC' | 'DESC'>('ASC');
  const [isLoading, setIsLoading] = useState(true);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [filterFlag, setFilterFlag] = useState<boolean>(false);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);

  useUrlSearchQuery((query) => {
    setSearchQuery(query);
    setPage(1);
  });

  // Fetch users whenever page, rowsPerPage, or searchQuery changes
  useEffect(() => {
    !filterFlag && fetchUsers(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  useEffect(() => {
    filterFlag &&
      fetchUsers(page, rowsPerPage, searchQuery, startDate, endDate);
  }, [startDate, endDate, page, rowsPerPage, searchQuery, sort]);

  // Fetch users from API
  const fetchUsers = async (
    page: number,
    limit: number,
    searchQuery?: string,
    SDate?: Date | null,
    EDate?: Date | null,
  ) => {
    try {
      setIsLoading(true);
      const response = await GETUsers({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
        startDate: SDate,
        endDate: EDate,
      });
      setUsers(response.data); // Set the user data from the API
      setTotalUsers(response.count); // Set the total number of users
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1); // API expects 1-based page index
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to the first page when rows per page changes
  };

  const refeshList = () => {
    fetchUsers(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search); // Update the search query state
    setPage(1); // Reset to the first page for new search
  }, 500); // 500ms delay before search executes

  const handleSortRefresh = () => {
    setSort(sort == 'ASC' ? 'DESC' : 'ASC');
    refeshList();
  };

  const handleresetFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setFilterFlag(false);
    refeshList();
  };

  const handleFilterRefresh = (Filter: CustomerDateFilterData) => {
    if (Filter.startDate && Filter.endDate) {
      setStartDate(new Date(Filter.startDate));
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    } else if (Filter.startDate) {
      setStartDate(new Date(Filter.startDate));
      setFilterFlag(true);
    } else if (Filter.endDate) {
      setEndDate(new Date(Filter.endDate));
      setFilterFlag(true);
    }
  };

  const handleAddModal = () => {
    setOpenAddModal(true);
  };
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <SmartFilter
        title="Users"
        createFilter={['SEARCH', 'ADD', 'FILTER']}
        // addElement={<CreateUserComp />}
        filterElement={
          <CustomerFilter
            onResetFilter={handleresetFilter}
            startDate={
              startDate ? new Date(startDate).toISOString().split('T')[0] : null
            }
            endDate={
              endDate ? new Date(endDate).toISOString().split('T')[0] : null
            }
          />
        }
        addbuttontitle="Add User"
        filterHeader={'Filter Users'}
        addHeader={'Add Users'}
        onSaved={handleSaveRefresh}
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
        onSort={handleSortRefresh}
        onNavigate={handleAddModal}
      />

      <CreateUserModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        onSaved={() => {
          handleSaveRefresh();
          setOpenAddModal(false);
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        {/* <CreateTableHeader tableCell={headerUserTable} /> */}
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {users.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Users Found
            </Typography>
          ) : (
            <UserList
              headerCells={headerUserTable}
              users={users}
              onUpdate={refeshList}
              isLoading={isLoading}
            />
          )}
        </Box>

        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalUsers}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(UserManagement);
